import type { HeaderImage, SitePageSummary } from './types';

const dimensions = { width: 1200, height: 630 };

const homeSocialImage: HeaderImage = {
  variant: 'home',
  src: '/seo/home.webp',
  alt: 'A luminous violet and magenta lotus on a dark field.',
  ...dimensions,
};

const alt = {
  en: {
    home: 'A luminous cyan, violet, and magenta circuit lotus over a midnight horizon.',
    explore: 'A midnight field map of connected cyan, violet, and magenta signal paths.',
    compare: 'Two luminous circuit paths meeting at a violet lotus node against midnight black.',
    understand: 'A cyan signal bloom with concentric violet paths on a midnight field.',
    procedures: 'A precise magenta and cyan circuit lattice forming a lotus-like aperture on midnight black.',
    planning: 'A midnight navigation grid with cyan route markers and violet signal arcs.',
    recovery: 'A calm violet circuit bloom rising through a midnight blue field.',
    access: 'A magenta threshold of connected circuit lights across a midnight horizon.',
    stories: 'Interwoven cyan and magenta signal threads around a violet lotus on midnight black.',
    tools: 'A cyan instrument panel of connected signal nodes and a violet circuit bloom.',
  },
  es: {
    home: 'Un loto de circuitos luminosos en cian, violeta y magenta sobre un horizonte de medianoche.',
    explore: 'Un mapa nocturno de rutas de señal conectadas en cian, violeta y magenta.',
    compare: 'Dos rutas de circuitos luminosos que se encuentran en un nodo de loto violeta sobre negro medianoche.',
    understand: 'Una flor de señal cian con rutas violetas concéntricas sobre un campo de medianoche.',
    procedures: 'Una retícula precisa de circuitos magenta y cian que forma una abertura parecida a un loto sobre negro medianoche.',
    planning: 'Una cuadrícula de navegación nocturna con marcadores de ruta cian y arcos de señal violeta.',
    recovery: 'Una tranquila flor de circuitos violeta que asciende por un campo azul de medianoche.',
    access: 'Un umbral magenta de luces de circuito conectadas sobre un horizonte de medianoche.',
    stories: 'Hilos de señal cian y magenta entrelazados alrededor de un loto violeta sobre negro medianoche.',
    tools: 'Un panel de instrumentos cian de nodos de señal conectados y una flor de circuitos violeta.',
  },
} as const;

type Variant = keyof typeof alt.en;

function variantFor(page: SitePageSummary): Variant {
  if (page.kind === 'home') return 'home';
  if (page.kind === 'explorer') return 'explore';
  if (page.kind === 'comparison') return 'compare';
  if (page.kind === 'procedure') return 'procedures';
  if (page.kind === 'story') return 'stories';
  if (page.kind === 'tool') return 'tools';
  if (page.collection in alt.en) return page.collection as Variant;
  return 'understand';
}

export function headerImageFor(page: SitePageSummary): HeaderImage {
  if (page.headerImage) return page.headerImage;
  const variant = variantFor(page);
  const language = page.lang === 'es' ? 'es' : 'en';
  return { variant, src: `/headers/${variant}.webp`, alt: alt[language][variant], ...dimensions };
}

export function seoFor(page: SitePageSummary | null) {
  if (!page) return { title: 'Page not found', description: 'The requested page could not be found.', image: homeSocialImage };
  if (page.kind === 'home') return {
    ...page.seo,
    title: page.lang === 'es' ? 'Guía de cirugía de afirmación de género' : 'Gender Affirming Surgery Guide',
    description: page.seo?.description ?? page.description,
    image: page.seo?.image ?? homeSocialImage,
  };
  if (page.seo) return page.seo;
  return { title: page.title, description: page.description, image: headerImageFor(page) };
}

export function absoluteAssetUrl(src: string) {
  return new URL(src, 'https://bottomsurgery.info').href;
}
